import {
  adminStatusBadgeRole,
  formatUsPhoneInput,
  isValidUsPhone,
} from '@tenfour/domain'

/** Simple class name joiner — no tailwind-merge needed */
export function cn(...inputs: (string | undefined | null | false)[]): string {
  return inputs.filter(Boolean).join(' ');
}

export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
}

export function formatDate(date: string | Date, format: 'short' | 'long' | 'datetime' = 'short'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (format === 'long') {
    return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  }
  if (format === 'datetime') {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatShortDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
}

/**
 * Progressive phone-as-you-type formatter — masks partial input so the user
 * sees the digits group as they go ("(320) 555-1234"). Used on text-input
 * onChange handlers, unlike formatPhone() which expects a complete number.
 * Delegates to @tenfour/domain (US/NANP only).
 */
export const formatPhoneInput = formatUsPhoneInput

/**
 * What to call a customer wherever a name renders (DES-35): the trimmed name,
 * else their phone (formatted), else "Unnamed customer" — never blank.
 */
export function displayCustomerName(name?: string | null, phone?: string | null): string {
  const trimmed = name?.trim();
  if (trimmed) return trimmed;
  const p = phone?.trim();
  if (p) return formatPhone(p);
  return 'Unnamed customer';
}

/** Returns true when the masked or raw value has exactly 10 US digits. */
export const isValidUSPhone = isValidUsPhone

/**
 * Lightweight client-side email-format check (mirrors the server's @IsEmail
 * gate enough to reject obviously-invalid input before submit). Not a full
 * RFC validator — the server stays the source of truth.
 */
export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/** Returns a CSS color string for use with style={{ background }} — raw DB status (admin). */
export function getStatusDot(status: string): string {
  switch (status) {
    case 'SENT':                return '#f59e0b'; // amber-500
    case 'DRAFT':
    case 'PENDING_REVIEW':
    case 'PENDING_CONFIRMATION': return '#8a9aa8'; // steel
    case 'QUOTE':               return '#6366f1'; // indigo-500
    case 'OVERDUE':             return '#ef4444'; // red-500
    case 'CANCELLED':
    case 'CANCELED':            return '#94a3b8'; // slate-400
    default:                    return '#111111'; // off-black
  }
}

/** Returns a CSS color string for use with style={{ color }} — raw DB status (admin). */
export function getStatusColor(status: string): string {
  switch (status) {
    case 'SENT':                return '#d97706'; // amber-600
    case 'DRAFT':
    case 'PENDING_REVIEW':
    case 'PENDING_CONFIRMATION': return '#8a9aa8'; // steel
    case 'QUOTE':               return '#4f46e5'; // indigo-600
    case 'OVERDUE':             return '#ef4444'; // red-500
    case 'CANCELLED':
    case 'CANCELED':            return '#64748b'; // slate-500
    default:                    return '#111111'; // off-black
  }
}

/** Human label for raw DB status — admin / internal ops. */
export function getStatusLabel(status: string): string {
  switch (status) {
    case 'SENT':                return 'Sent';
    case 'DRAFT':               return 'Draft';
    case 'SENDING':             return 'On its way';
    case 'QUOTE':               return 'Quote';
    case 'PAID_PARTIAL':        return 'Partially Paid';
    case 'PAID':                return 'Paid';
    case 'PENDING_REVIEW':      return 'Pending Review';
    case 'PENDING_CONFIRMATION': return 'Awaiting Account';
    case 'OVERDUE':             return 'Overdue';
    case 'CANCELLED':
    case 'CANCELED':            return 'Canceled';
    case 'ACTIVE':              return 'Active';
    case 'TRIAL':               return 'Trial';
    case 'PENDING':             return 'Pending';
    case 'PAST_DUE':            return 'Past Due';
    case 'DONE':                return 'Done';
    default:                    return status;
  }
}

/** Dot color for contractor-facing display status. */
export function getDisplayStatusDot(displayStatus: string): string {
  switch (displayStatus) {
    case 'Paid':          return '#10b981'; // emerald-500
    case 'Partially Paid':return '#14b8a6'; // teal-500
    case 'Sent':          return '#f59e0b'; // amber-500
    case 'Draft':         return '#8a9aa8'; // steel
    case 'In review':     return '#f59e0b'; // amber — with Ten Four ops
    case 'On its way':    return '#3b82f6'; // blue-500
    case 'Quote':         return '#6366f1'; // indigo-500
    case 'Overdue':       return '#ef4444'; // red-500
    case 'Canceled':      return '#94a3b8'; // slate-400
    default:              return '#111111';
  }
}

/** Text color for contractor-facing display status. */
export function getDisplayStatusColor(displayStatus: string): string {
  switch (displayStatus) {
    case 'Paid':          return '#059669'; // emerald-600
    case 'Partially Paid':return '#0d9488'; // teal-600
    case 'Sent':          return '#d97706'; // amber-600
    case 'Draft':         return '#8a9aa8';
    case 'In review':     return '#b45309'; // amber-700
    case 'On its way':    return '#2563eb'; // blue-600
    case 'Quote':         return '#4f46e5';
    case 'Overdue':       return '#ef4444';
    case 'Canceled':      return '#64748b';
    default:              return '#111111';
  }
}

/** Label for contractor-facing display status (already human-readable). */
export function getDisplayStatusLabel(displayStatus: string): string {
  return displayStatus || 'Draft';
}

/** Maps a raw status onto an admin.module.css badge class (DES-10).
 *  Role → class lives here so CSS modules stay web-local; the role mapping
 *  itself is pure domain (`adminStatusBadgeRole`) and pinned by Jest. */
export function getStatusBadgeClass(status: string, styles: Record<string, string>): string {
  switch (adminStatusBadgeRole(status)) {
    case 'done':
      return styles.badgeSent
    case 'invoice_sent':
      return styles.badgeInvoiceSent ?? styles.badgeSent
    case 'paid':
      return styles.badgePaid ?? styles.badgeSent
    case 'pending_review':
      return styles.badgePendingReview
    case 'pending_confirmation':
      return styles.badgePendingConfirmation
    case 'canceled':
      return styles.badgeCanceled
    default:
      return styles.badgeDefault
  }
}

export function formatRelative(dateStr: string | null): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function waitTimeText(createdAt: string): string {
  const diffMs = Date.now() - new Date(createdAt).getTime();
  const totalMins = Math.floor(diffMs / 60000);
  if (totalMins < 1) return 'just now';
  if (totalMins < 60) return `${totalMins}m`;
  const hours = Math.floor(totalMins / 60);
  if (hours < 24) return `${hours}h ${totalMins % 60}m`;
  return `${Math.floor(hours / 24)}d`;
}

export function truncate(str: string, len: number): string {
  return str.length <= len ? str : str.slice(0, len) + '…';
}

export function maskPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return phone;
  return `••••${digits.slice(-4)}`;
}
